import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
  createGeoPointFromString,
  formatGeoPoint,
  geoPointPasteEventHandler,
  geoPointValueBlur,
  geoPointValueChange,
  isGeoPoint,
} from '@blaze-shared/shared-feature';
import { GeoPoint } from '@blaze-shared/shared-util';

import { BlazeTableValue } from '../../../shared/blaze-table.service';
import { BaseCellInputComponent } from '../base-cell-input.component';

@UntilDestroy()
@Component({
  selector: 'blaze-cell-input-geo-point',
  template: `<input
    #geoPointInput
    type="text"
    [placeholder]="placeholder"
    (beforeinput)="geoPointInputTransformFn($event, locationInputDisplayValue)"
    (blur)="geoPointValueBlurFn(locationInputDisplayValue)"
    (paste)="geoPointPasteFn($event, locationInputDisplayValue)"
    [formControl]="locationInputDisplayValue"
    [class.readonly]="column.isComputed"
    [attr.readonly]="column.isComputed ? true : null"
    class="input-string"
  />`,
  styleUrls: ['../base-cell-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule],
})
export class CellInputGeoPointComponent extends BaseCellInputComponent<string> implements OnInit {
  @Input() placeholder = '';

  @ViewChild('geoPointInput') geoPointInputRef!: ElementRef<HTMLInputElement>;

  geoPointInputTransformFn = geoPointValueChange;
  geoPointPasteFn = geoPointPasteEventHandler;
  geoPointValueBlurFn = geoPointValueBlur;

  locationInputDisplayValue!: UntypedFormControl;

  override get nativeElement(): HTMLInputElement | null {
    return this.geoPointInputRef?.nativeElement || null;
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.locationInputDisplayValue = this.createLocationInputFormControl();
  }

  private getValueForLocationInput(value: BlazeTableValue): string {
    if (!isGeoPoint(value)) {
      return '';
    }
    return formatGeoPoint(value);
  }

  private getValueToEmit(value: string | null): GeoPoint | null {
    return createGeoPointFromString(value || '');
  }

  private createLocationInputFormControl(): FormControl {
    const formControl = new FormControl({
      value: this.getValueForLocationInput(this.value),
      disabled: this.isDisabled,
    });

    formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      const newValue = this.getValueToEmit(value);

      this.formControl.setValue(newValue, { emitEvent: true });
    });

    return formControl;
  }
}
